import React, { createContext, useContext, useEffect, useMemo, useState } from 'react';
import { DEVELOPMENT_CONFIG } from '../services/installService';
import { DEFAULT_LOCALIZATION, DEFAULT_SECURITY, loadSettings, saveSection } from '../services/platformSettingsStore';
import type { PublicPlatformConfig, SecuritySettings, SettingsSection } from '../types/install';

/**
 * The runtime platform configuration.
 *
 * Site title, metadata, branding, locale defaults and the administration URL
 * are configuration read at runtime — not constants compiled into the bundle.
 * That is what lets the same build be deployed to another domain, and lets an
 * administrator change the site title without a rebuild.
 *
 * The configuration carried here is public by construction. Database
 * credentials, session secrets and encryption keys have no representation in
 * it, so there is no path by which they could reach a browser.
 */
interface PlatformConfigValue {
  config: PublicPlatformConfig;
  /** Policy, not secrets. There is no secret in this type. */
  security: SecuritySettings;
  ready: boolean;
  /** Whether the values in use are published configuration, this browser's last
   * known copy, or development defaults. Shown on screen rather than assumed. */
  source: 'server' | 'local' | 'defaults';
  error: string | null;
  refresh: () => Promise<void>;
  /** Saves one section and applies it to the running product immediately. */
  save: (section: SettingsSection, value: unknown, reason: string, changedBy: string) => Promise<void>;
}
const Ctx = createContext<PlatformConfigValue | null>(null);

/** Writes the document head from configuration. Called on every change. */
function applyMetadata(config: PublicPlatformConfig): void {
  const {
    seo
  } = config;
  document.title = seo.metaTitle || seo.siteTitle || config.platform.name;
  const set = (selector: string, attr: string, value: string, create: () => HTMLElement) => {
    if (!value) return;
    let element = document.head.querySelector(selector);
    if (!element) {
      element = create();
      document.head.appendChild(element);
    }
    element.setAttribute(attr, value);
  };
  set('meta[name="description"]', 'content', seo.metaDescription, () => {
    const meta = document.createElement('meta');
    meta.setAttribute('name', 'description');
    return meta;
  });
  set('meta[name="robots"]', 'content', seo.robots, () => {
    const meta = document.createElement('meta');
    meta.setAttribute('name', 'robots');
    return meta;
  });
  const og: Array<[string, string]> = [['og:site_name', config.platform.name], ['og:title', seo.socialTitle || seo.metaTitle], ['og:description', seo.socialDescription || seo.metaDescription], ['og:image', seo.socialImageUrl], ['og:url', seo.siteUrl], ['twitter:card', seo.socialImageUrl ? 'summary_large_image' : 'summary'], ['twitter:title', seo.socialTitle || seo.metaTitle], ['twitter:description', seo.socialDescription || seo.metaDescription], ['twitter:image', seo.socialImageUrl]];
  for (const [property, value] of og) {
    set(`meta[property="${property}"]`, 'content', value, () => {
      const meta = document.createElement('meta');
      meta.setAttribute('property', property);
      return meta;
    });
  }
  if (seo.faviconUrl) {
    let link = document.head.querySelector('link[rel="icon"]');
    if (!link) {
      link = document.createElement('link');
      link.setAttribute('rel', 'icon');
      document.head.appendChild(link);
    }
    link.setAttribute('href', seo.faviconUrl);
  }
}
export function PlatformConfigProvider({
  children
}: {
  children: React.ReactNode;
}) {
  const [config, setConfig] = useState<PublicPlatformConfig>({
    ...DEVELOPMENT_CONFIG,
    localization: DEFAULT_LOCALIZATION
  });
  const [security, setSecurity] = useState<SecuritySettings>(DEFAULT_SECURITY);
  const [source, setSource] = useState<'server' | 'local' | 'defaults'>('defaults');
  const [error, setError] = useState<string | null>(null);
  const [ready, setReady] = useState(false);
  const refresh = React.useCallback(async () => {
    const result = await loadSettings();
    setConfig(result.config);
    setSecurity(result.security);
    setSource(result.source);
    setError(result.error);
    setReady(true);
    applyMetadata(result.config);
  }, []);
  useEffect(() => {
    refresh();
  }, [refresh]);

  /**
   * A saved section is applied to the running product at once — the title in
   * the tab, the metadata, the administration URL — because configuration that
   * needs a rebuild to take effect is not configuration.
   */
  const save = React.useCallback(async (section: SettingsSection, value: unknown, reason: string, changedBy: string) => {
    const stored = await saveSection(section, value, {
      reason,
      changedBy,
      current: {
        config,
        security
      }
    });
    setConfig(stored.config);
    setSecurity(stored.security);
    applyMetadata(stored.config);
  }, [config, security]);
  const value = useMemo<PlatformConfigValue>(() => ({
    config,
    security,
    ready,
    source,
    error,
    refresh,
    save
  }), [config, security, ready, source, error, refresh, save]);
  return <Ctx.Provider value={value} data-id="element-5538" data-mp="1sgwu303tn2ek">{children}</Ctx.Provider>;
}
export function usePlatformConfig(): PlatformConfigValue {
  const value = useContext(Ctx);
  if (!value) throw new Error('usePlatformConfig must be used inside PlatformConfigProvider');
  return value;
}

/** Where administration lives on this deployment. Never assumed. */
export function useAdminSlug(): string {
  return usePlatformConfig().config.adminSlug;
}